import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiRefreshCw, FiX, FiDownload } from 'react-icons/fi';

// Session storage key for hiding the banner
const HIDE_BANNER_KEY = 'hideUpdateBanner';

const slideDown = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-20px);
    opacity: 0;
  }
`;

// Outer container for positioning
const BannerWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  justify-content: center;
  padding: 12px 16px;
  pointer-events: none;

  @media (min-width: 768px) {
    padding: 16px 24px;
  }
`;

// The card-style banner
const BannerCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 1100px;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 12px 16px;
  pointer-events: auto;
  animation: ${props => props.$isClosing ? slideUp : slideDown} 0.2s ease-out forwards;
  @media (min-width: 640px) {
    padding-right: 52px;
  }
  @media (min-width: 768px) {
    padding: 14px 52px 14px 20px;
    border-radius: 14px;
  }
`;

// Icon container with brand styling
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(216, 184, 137, 0.15) 0%, rgba(176, 141, 87, 0.15) 100%);
  border-radius: 10px;
  flex-shrink: 0;
  color: var(--primary-color);

  svg {
    width: 20px;
    height: 20px;
  }

  @media (min-width: 768px) {
    width: 44px;
    height: 44px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

// Text content area
const TextContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.4;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const Description = styled.p`
  margin: 2px 0 0 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;

  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

// Action buttons container
const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 12px;

  @media (min-width: 640px) {
    margin-top: 0;
    margin-left: auto;
  }
`;

// Primary button (Refresh Now)
const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(176, 141, 87, 0.25);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    padding: 8px 16px;
    font-size: 0.875rem;
  }
`;

// Secondary button (Later)
const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--text-muted);
  }

  &:active {
    background: var(--background-tertiary);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    padding: 8px 16px;
    font-size: 0.875rem;
  }
`;

// Dismiss (X) button
const DismissButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: var(--background-secondary);
    color: var(--text-primary);
  }

  &:active {
    background: var(--background-tertiary);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    width: 34px;
    height: 34px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

// Mobile layout wrapper
const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
  }
`;

const MainContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;

  @media (min-width: 640px) {
    align-items: center;
  }
`;

const UpdateNotification = ({
  isVisible,
  onRefresh,
  onDismiss,
  onLater,
  autoRefresh = false,
  countdown = 0
}) => {
  const [timeLeft, setTimeLeft] = useState(countdown);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Check sessionStorage on mount to see if banner was dismissed this session
  useEffect(() => {
    const isHidden = sessionStorage.getItem(HIDE_BANNER_KEY) === 'true';
    if (isVisible && !isHidden) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (!isVisible) {
      // Start close animation
      if (shouldRender) {
        setIsClosing(true);
        const timer = setTimeout(() => {
          setShouldRender(false);
        }, 200); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, shouldRender]);

  // Auto-refresh countdown
  useEffect(() => {
    if (autoRefresh && countdown > 0) {
      setTimeLeft(countdown);
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            onRefresh();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [autoRefresh, countdown, onRefresh]);

  // Handle dismiss with sessionStorage
  const handleDismissClick = () => {
    sessionStorage.setItem(HIDE_BANNER_KEY, 'true');
    setIsClosing(true);
    setTimeout(() => {
      onDismiss();
    }, 200);
  };

  // Handle later with sessionStorage
  const handleLaterClick = () => {
    sessionStorage.setItem(HIDE_BANNER_KEY, 'true');
    setIsClosing(true);
    setTimeout(() => {
      onLater();
    }, 200);
  };

  // Check if hidden in sessionStorage
  const isHiddenInSession = sessionStorage.getItem(HIDE_BANNER_KEY) === 'true';

  if (!shouldRender || isHiddenInSession) return null;

  return (
    <BannerWrapper
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <BannerCard $isClosing={isClosing}>
        <DismissButton
          onClick={handleDismissClick}
          aria-label="Dismiss update banner"
          title="Dismiss"
        >
          <FiX />
        </DismissButton>
        <MobileLayout>
          <MainContent>
            <IconWrapper>
              <FiDownload />
            </IconWrapper>
            <TextContent>
              <Title>New version available</Title>
              <Description>
                Refresh to get the latest features and fixes.
                {autoRefresh && timeLeft > 0 && (
                  <span> Auto-refreshing in {timeLeft}s...</span>
                )}
              </Description>
            </TextContent>
          </MainContent>

          <ActionButtons>
            <PrimaryButton onClick={onRefresh}>
              <FiRefreshCw />
              <span>Refresh now</span>
            </PrimaryButton>

            {!autoRefresh && (
              <SecondaryButton onClick={handleLaterClick}>
                Later
              </SecondaryButton>
            )}

          </ActionButtons>
        </MobileLayout>
      </BannerCard>
    </BannerWrapper>
  );
};

export default UpdateNotification;
